import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBoxOpen, faGift, faUserCircle, faDollarSign, faPuzzlePiece,
    faFilter, faLayerGroup, faShareAlt, faPiggyBank, faCheckCircle,
    faSearch, faEye, faGrip, faList, faCheck, faStore,
    faBox, faCircleHalfStroke,
} from '@fortawesome/free-solid-svg-icons';

// ─── Sample data (field names match the real UserSetsView data shape) ────────

const SAMPLE_SETS = [
    {
        name: 'Millennium Falcon',   set_num: '75192-1', year: 2017, theme_name: 'Star Wars',
        num_parts: 7541, num_minifigures: 8,
        owned_minifigures: 14, expected_minifigures: 16,
        retail_price: 849.99, sealed_value: 1249.00, used_value: 639.00,
        retired: true,  quantity: 2, sealed: 1, complete: 1,
        img: 'https://cdn.rebrickable.com/media/sets/75192-1.jpg',
    },
    {
        name: 'Eiffel Tower',        set_num: '10307-1', year: 2022, theme_name: 'Icons',
        num_parts: 10001, num_minifigures: 0,
        retail_price: 629.99, sealed_value: null,   used_value: null,
        retired: false, quantity: 1, sealed: 0, complete: 1,
        img: 'https://cdn.rebrickable.com/media/sets/10307-1.jpg',
    },
    {
        name: 'Hogwarts Castle',     set_num: '71043-1', year: 2018, theme_name: 'Harry Potter',
        num_parts: 6020, num_minifigures: 4,
        owned_minifigures: 4, expected_minifigures: 4,
        retail_price: 469.99, sealed_value: 699.00, used_value: 379.00,
        retired: true,  quantity: 1, sealed: 0, complete: 1,
        img: 'https://cdn.rebrickable.com/media/sets/71043-1.jpg',
    },
    {
        name: 'Porsche 911 GT3 RS',  set_num: '42096-1', year: 2018, theme_name: 'Technic',
        num_parts: 1580, num_minifigures: 0,
        retail_price: 299.99, sealed_value: 449.00, used_value: 219.00,
        retired: true,  quantity: 1, sealed: 1, complete: 0,
        img: 'https://cdn.rebrickable.com/media/sets/42096-1.jpg',
    },
    {
        name: 'Colosseum',           set_num: '10276-1', year: 2020, theme_name: 'Icons',
        num_parts: 9036, num_minifigures: 0,
        retail_price: 549.99, sealed_value: null,   used_value: null,
        retired: false, quantity: 1, sealed: 0, complete: 1,
        img: 'https://cdn.rebrickable.com/media/sets/10276-1.jpg',
    },
    {
        name: 'Wild Flower Bouquet', set_num: '10280-1', year: 2021, theme_name: 'Icons',
        num_parts: 939,  num_minifigures: 0,
        retail_price: 49.99,  sealed_value: 89.00,  used_value: 54.00,
        retired: true,  quantity: 1, sealed: 0, complete: 1,
        img: 'https://cdn.rebrickable.com/media/sets/10280-1.jpg',
    },
];

const WISHLIST_SAMPLE_SETS = [
    {
        name: 'Rivendell',           set_num: '10316-1', year: 2023, theme_name: 'Icons',
        num_parts: 6167, num_minifigures: 15,
        retail_price: 499.99, sealed_value: null,   used_value: null,
        retired: false,
        img: 'https://cdn.rebrickable.com/media/sets/10316-1.jpg',
    },
    {
        name: 'The Razor Crest',     set_num: '75331-1', year: 2022, theme_name: 'Star Wars',
        num_parts: 6187, num_minifigures: 4,
        retail_price: 599.99, sealed_value: 749.00, used_value: 449.00,
        retired: true,
        img: 'https://cdn.rebrickable.com/media/sets/75331-1.jpg',
    },
    {
        name: 'Tranquil Garden',     set_num: '10315-1', year: 2023, theme_name: 'Icons',
        num_parts: 1363, num_minifigures: 0,
        retail_price: 99.99,  sealed_value: null,   used_value: null,
        retired: false,
        img: 'https://cdn.rebrickable.com/media/sets/10315-1.jpg',
    },
    {
        name: 'Land Rover Defender', set_num: '42110-1', year: 2019, theme_name: 'Technic',
        num_parts: 2573, num_minifigures: 0,
        retail_price: 299.99, sealed_value: 429.00, used_value: 239.00,
        retired: true,
        img: 'https://cdn.rebrickable.com/media/sets/42110-1.jpg',
    },
    {
        name: 'Statue of Liberty',   set_num: '21042-1', year: 2018, theme_name: 'Architecture',
        num_parts: 1685, num_minifigures: 0,
        retail_price: 119.99, sealed_value: 189.00, used_value: 99.00,
        retired: true,
        img: 'https://cdn.rebrickable.com/media/sets/21042-1.jpg',
    },
    {
        name: 'Wildflower Bouquet',  set_num: '10313-1', year: 2023, theme_name: 'Icons',
        num_parts: 939,  num_minifigures: 0,
        retail_price: 59.99,  sealed_value: null,   used_value: null,
        retired: false,
        img: 'https://cdn.rebrickable.com/media/sets/10313-1.jpg',
    },
];

/** Same market-value rules as UserSetsView `renderStatsCards` (sealed / used / retail fallbacks). */
function computeGuestLandingCollectionValue(sets) {
    return sets.reduce((acc, set) => {
        const sealedCount = Number(set.sealed) || 0;
        const completeCount = Number(set.complete) || 0;
        const nonSealedCompleteCount = completeCount - sealedCount;
        const incompleteCount = Number(set.quantity) - completeCount;

        const sealedValue = parseFloat(set.sealed_value) || 0;
        const usedValue = parseFloat(set.used_value) || 0;
        const retailPrice = parseFloat(set.retail_price) || 0;

        let setValue = 0;

        if (sealedCount > 0) {
            setValue += sealedCount * (sealedValue > 0 ? sealedValue : retailPrice);
        }
        if (nonSealedCompleteCount > 0) {
            setValue += nonSealedCompleteCount * (usedValue > 0 ? usedValue : retailPrice);
        }
        if (incompleteCount > 0) {
            setValue += incompleteCount * (usedValue > 0 ? usedValue : retailPrice);
        }

        return acc + setValue;
    }, 0);
}

// Smaller ® next to display-size “LEGO” so the mark matches typical trademark styling.
const LegoWordmark = () => (
    <>
        LEGO
        <span className="text-[0.5em] align-super font-black leading-none ml-px">®</span>
    </>
);

// ─── Features data ───────────────────────────────────────────────────────────

const COLLECTION_FEATURES = [
    { icon: faBoxOpen,      color: 'bg-red-100 text-red-600',    title: 'Track Sealed vs. Opened Sets',  desc: 'For each set, mark individual copies as sealed, opened, or complete. Ideal for collectors who keep investment copies sealed alongside opened display builds.' },
    { icon: faPuzzlePiece,  color: 'bg-purple-100 text-purple-600', title: 'Minifigure Inventory',        desc: "See every minifigure included in each set and check off the ones you own. Perfect for tracking which figures you're still missing." },
    { icon: faDollarSign,   color: 'bg-green-100 text-green-600',  title: 'Set Values & Prices',          desc: 'View retail prices alongside current sealed and used aftermarket values for retired sets. Know exactly what your collection is worth.' },
    { icon: faFilter,       color: 'bg-blue-100 text-blue-600',    title: 'Filter & Sort Everything',     desc: 'Sort by theme, year, piece count, or value. Filter by completion status, sealed state, or decade. Grid or list view, it\'s your choice.' },
    { icon: faLayerGroup,   color: 'bg-yellow-100 text-yellow-600',title: 'Organized by Theme',           desc: 'Your collection is automatically grouped by theme, making it easy to browse your Star Wars haul, Technic builds, or City sets all at once.' },
    { icon: faShareAlt,     color: 'bg-orange-100 text-orange-600',title: 'Share Your Collection',        desc: 'Make your collection public and share a direct link with friends, family, or the LEGO community. Show off what you have built over the years.' },
];

const WISHLIST_FEATURES = [
    { icon: faGift,         color: 'bg-red-100 text-red-600',     title: 'The Perfect Gift Guide',           desc: 'Share your wishlist with family before birthdays and holidays. No more duplicate or unwanted sets. They see exactly which LEGO® sets you actually want.' },
    { icon: faDollarSign,   color: 'bg-green-100 text-green-600', title: 'Monitor Prices Before You Buy',    desc: "Add sets to your wishlist and check back to watch prices. When a retired set's value is rising, you'll know it's time to buy." },
    { icon: faPiggyBank,    color: 'bg-yellow-100 text-yellow-600',title: 'Plan Future Purchases',           desc: 'Keep a running list of sets you want without committing to buy. Browse themes, find what excites you, and save it for later.' },
    { icon: faCheckCircle,  color: 'bg-blue-100 text-blue-600',   title: 'Move to Collection in One Click', desc: "Once you've bought a wishlist set, move it to your collection instantly. Quantity, condition tracking, and minifigure inventory all carry over." },
    { icon: faSearch,       color: 'bg-purple-100 text-purple-600',title: 'Search 25,000+ Sets',            desc: 'Find any LEGO® set by name, number, or theme and add it to your wishlist in seconds. Every set ever released is in the database.' },
    { icon: faEye,          color: 'bg-orange-100 text-orange-600',title: 'Public or Private',              desc: "Keep your wishlist to yourself, or make it public so the LEGO community can see what you're hunting for next." },
];


// ─── Grid card — mirrors renderSetCard exactly ────────────────────────────────

const MockSetCard = ({ set, isWishlist, showPrices }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md">
        <div className="relative">
            {/* Image */}
            <div className="p-2 h-48 flex items-center justify-center relative">
                <img
                    src={set.img}
                    alt={set.name}
                    className="h-full object-contain max-w-full"
                    onError={(e) => { e.target.src = '/images/lego_piece_questionmark.png'; }}
                />
                {/* Quantity badge */}
                {!isWishlist && set.quantity > 1 && (
                    <div className="absolute top-2 right-2 bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 min-h-10 min-w-10 px-3 rounded-full flex items-center justify-center font-extrabold text-base shadow-xl border-2 border-white">
                        ×{set.quantity}
                    </div>
                )}
            </div>

            {/* Details */}
            <div className="p-3 sm:p-4 pt-0 bg-gray-50/50">
                <h3 className="font-medium text-gray-800 mb-1 line-clamp-2 text-sm sm:text-base leading-tight" style={{ minHeight: '2.5rem' }}>
                    {set.name}
                </h3>

                <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm text-gray-600 font-mono truncate flex-shrink">{set.set_num}</span>
                    <span className="text-xs sm:text-sm bg-gray-100 px-2 py-0.5 sm:py-1 rounded-lg ml-2 flex-shrink-0">{set.year}</span>
                </div>

                <div className="space-y-2 mb-4">
                    {/* Stats row: pieces + minifigs */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs sm:text-sm font-semibold rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 flex items-center shadow-sm border border-red-600/20">
                            <FontAwesomeIcon icon={faPuzzlePiece} className="mr-1 sm:mr-2 text-red-100" />
                            <span className="whitespace-nowrap">{Number(set.num_parts).toLocaleString()}</span>
                        </div>
                        {set.num_minifigures > 0 && (
                            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs sm:text-sm font-semibold rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 flex items-center shadow-sm border border-purple-600/20">
                                <FontAwesomeIcon icon={faUserCircle} className="mr-1 sm:mr-2 opacity-90" />
                                <span className="whitespace-nowrap">{set.num_minifigures}</span>
                            </div>
                        )}
                    </div>

                    {/* Collection status row */}
                    {!isWishlist && (
                        <div className="flex flex-col sm:flex-row items-stretch gap-2">
                            {/* Completion */}
                            <div className="flex-1">
                                <div className={`text-xs sm:text-sm font-medium rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex items-center justify-center shadow-sm border min-h-[2rem] ${
                                    Number(set.complete) === Number(set.quantity)
                                        ? 'bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200'
                                        : Number(set.complete) > 0
                                            ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 border-blue-200'
                                            : 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 border-amber-200'
                                }`}>
                                    <FontAwesomeIcon
                                        icon={Number(set.complete) === Number(set.quantity) ? faCheckCircle : faCircleHalfStroke}
                                        className="mr-1 sm:mr-2 flex-shrink-0"
                                    />
                                    <span className="font-semibold whitespace-nowrap">
                                        {Number(set.complete) === Number(set.quantity)
                                            ? 'Complete'
                                            : Number(set.complete) > 0
                                                ? `${set.complete}/${set.quantity}`
                                                : 'Incomplete'}
                                    </span>
                                </div>
                            </div>
                            {/* Sealed */}
                            <div className="flex-1">
                                <div className={`text-xs sm:text-sm font-medium rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex items-center justify-center shadow-sm border min-h-[2rem] ${
                                    Number(set.sealed) > 0
                                        ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 border-blue-200'
                                        : 'bg-gradient-to-r from-slate-50 to-slate-100 text-slate-700 border-slate-200'
                                }`}>
                                    <FontAwesomeIcon
                                        icon={Number(set.sealed) > 0 ? faBox : faBoxOpen}
                                        className="mr-1 sm:mr-2 flex-shrink-0"
                                    />
                                    <span className="font-semibold whitespace-nowrap">
                                        {Number(set.sealed) === Number(set.quantity)
                                            ? 'Sealed'
                                            : Number(set.sealed) > 0
                                                ? `${set.sealed}/${set.quantity}`
                                                : 'Opened'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Prices section */}
                {showPrices && (
                    <div className="border-t border-gray-100 pt-2 sm:pt-3 mb-1">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
                            {set.retail_price && (
                                <div className="text-center sm:text-left">
                                    <div className="text-xs text-gray-500 mb-0.5">Retail</div>
                                    <div className="font-medium truncate">${Number(set.retail_price).toFixed(2)}</div>
                                </div>
                            )}
                            {set.sealed_value && (
                                <div className="text-center sm:text-left">
                                    <div className="text-xs text-gray-500 mb-0.5">Sealed</div>
                                    <div className="font-medium text-green-600 truncate">${Number(set.sealed_value).toFixed(2)}</div>
                                </div>
                            )}
                            {set.used_value && (
                                <div className="text-center sm:text-left col-span-2 sm:col-span-1">
                                    <div className="text-xs text-gray-500 mb-0.5">Used</div>
                                    <div className="font-medium text-blue-600 truncate">${Number(set.used_value).toFixed(2)}</div>
                                </div>
                            )}
                            {!set.sealed_value && !set.used_value && (
                                <div className="col-span-2 sm:col-span-2 flex items-center gap-1">
                                    <FontAwesomeIcon icon={faStore} className="text-blue-400 text-xs" />
                                    <span className="text-blue-600 font-medium text-xs">Available in stores</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
);

// ─── List row — mirrors renderListRow exactly ─────────────────────────────────

const MockListRow = ({ set, isWishlist, showPrices, isFirst }) => {
    const isIncomplete = !isWishlist && Number(set.complete) < Number(set.quantity);
    return (
        <tr className={`border-b border-gray-100 transition-colors duration-150 hover:bg-gray-50 ${isIncomplete ? 'border-l-[3px] border-l-amber-400' : ''} ${!isFirst ? '' : ''}`}>
            {/* Thumbnail */}
            <td className="px-2 py-1.5 w-14">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 overflow-hidden flex-shrink-0">
                    <img
                        src={set.img}
                        alt={set.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.src = '/images/lego_piece_questionmark.png'; }}
                    />
                </div>
            </td>

            {/* Set # */}
            <td className="px-3 py-2 whitespace-nowrap">
                <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-mono text-gray-500">{set.set_num}</span>
                    {!isWishlist && Number(set.quantity) > 1 && (
                        <span className="inline-flex items-center bg-amber-100 text-amber-800 text-xs font-bold px-1.5 py-0.5 rounded-full w-fit">
                            ×{set.quantity}
                        </span>
                    )}
                </div>
            </td>

            {/* Name */}
            <td className="px-3 py-2 min-w-[140px]">
                <span className="text-sm font-semibold text-gray-800 leading-tight">{set.name}</span>
            </td>

            {/* Year */}
            <td className="px-3 py-2 text-center whitespace-nowrap">
                <span className="text-sm text-gray-600 font-medium bg-gray-100 px-2 py-0.5 rounded-md">{set.year}</span>
            </td>

            {/* Pieces */}
            <td className="px-3 py-2 text-center whitespace-nowrap">
                <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs font-semibold px-2 py-1 rounded-lg border border-red-100">
                    <FontAwesomeIcon icon={faPuzzlePiece} className="text-red-400 text-xs" />
                    {Number(set.num_parts).toLocaleString()}
                </span>
            </td>

            {/* Minifigs */}
            <td className="px-3 py-2 text-center whitespace-nowrap">
                {set.num_minifigures > 0 ? (
                    <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 text-xs font-semibold px-2 py-1 rounded-lg border border-purple-100">
                        <FontAwesomeIcon icon={faUserCircle} className="text-purple-400 text-xs" />
                        {set.num_minifigures}
                    </span>
                ) : (
                    <span className="text-gray-300 text-xs">—</span>
                )}
            </td>

            {/* Status (collection only) */}
            {!isWishlist && (
                <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg border whitespace-nowrap ${
                            Number(set.complete) === Number(set.quantity)
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : Number(set.complete) > 0
                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                            <FontAwesomeIcon
                                icon={Number(set.complete) === Number(set.quantity) ? faCheckCircle : faCircleHalfStroke}
                                className="text-xs"
                            />
                            {Number(set.complete) === Number(set.quantity)
                                ? 'Complete'
                                : Number(set.complete) > 0
                                    ? `${set.complete}/${set.quantity} built`
                                    : 'Incomplete'}
                        </span>
                        {Number(set.sealed) > 0 && (
                            <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 border border-sky-200 text-xs font-semibold px-2 py-1 rounded-lg whitespace-nowrap">
                                <FontAwesomeIcon icon={faBox} className="text-sky-400 text-xs" />
                                {Number(set.sealed) === Number(set.quantity) ? 'Sealed' : `${set.sealed} sealed`}
                            </span>
                        )}
                    </div>
                </td>
            )}

            {/* Prices */}
            {showPrices && (
                <>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span className="text-sm text-gray-700 font-medium">
                            {set.retail_price ? `$${Number(set.retail_price).toFixed(2)}` : <span className="text-gray-300">—</span>}
                        </span>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span className="text-sm text-green-600 font-semibold">
                            {set.sealed_value ? `$${Number(set.sealed_value).toFixed(2)}` : <span className="text-gray-300">—</span>}
                        </span>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span className="text-sm text-blue-600 font-semibold">
                            {set.used_value ? `$${Number(set.used_value).toFixed(2)}` : <span className="text-gray-300">—</span>}
                        </span>
                    </td>
                </>
            )}
        </tr>
    );
};


// ─── Main component ───────────────────────────────────────────────────────────

const GuestLanding = ({ isWishlist }) => {
    const [viewMode, setViewMode]     = useState('grid');
    const [showPrices, setShowPrices] = useState(true);

    const sampleSets  = isWishlist ? WISHLIST_SAMPLE_SETS : SAMPLE_SETS;
    const features    = isWishlist ? WISHLIST_FEATURES    : COLLECTION_FEATURES;
    const pageVerb    = isWishlist ? 'Wishlist' : 'Collection';

    // Match UserSetsView aggregation (quantity-weighted parts; minifig sums like the real API fields)
    const collectionTotals = !isWishlist
        ? sampleSets.reduce(
              (acc, s) => {
                  const q = Number(s.quantity || 1);
                  const ownedFig = s.owned_minifigures != null && s.owned_minifigures !== ''
                      ? Number(s.owned_minifigures)
                      : 0;
                  const expectedFig =
                      s.expected_minifigures != null && s.expected_minifigures !== ''
                          ? Number(s.expected_minifigures)
                          : (s.num_minifigures || 0) * q;
                  return {
                      totalSetCopies: acc.totalSetCopies + q,
                      totalParts: acc.totalParts + (s.num_parts || 0) * q,
                      sealedCopies: acc.sealedCopies + Number(s.sealed || 0),
                      ownedMinifigs: acc.ownedMinifigs + ownedFig,
                      expectedMinifigs: acc.expectedMinifigs + expectedFig,
                  };
              },
              {
                  totalSetCopies: 0,
                  totalParts: 0,
                  sealedCopies: 0,
                  ownedMinifigs: 0,
                  expectedMinifigs: 0,
              }
          )
        : null;

    const collectionTotalsWithValue =
        collectionTotals != null
            ? { ...collectionTotals, totalValue: computeGuestLandingCollectionValue(SAMPLE_SETS) }
            : null;

    return (
        <div className="min-h-screen bg-gray-50">

            {/* ── Hero ─────────────────────────────────────────────────────── */}
            <section className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-12 md:py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                        Your <LegoWordmark />{' '}
                        {isWishlist ? 'Wishlist, Ready to Share' : 'Collection, Perfectly Organized'}
                    </h1>
                    <p className="text-xl text-red-100 mb-6 max-w-2xl mx-auto leading-relaxed">
                        {isWishlist
                            ? 'Track every LEGO® set you want to buy. Share your wishlist with family for the perfect gift, monitor prices for retired sets, and plan your next purchase.'
                            : 'Track every LEGO® set you own with sealed/opened status, piece completion, minifigure inventory, and current market values. Never lose track of your brick collection again.'}
                    </p>
                    <p className="text-red-200 text-sm mb-8">
                        Free forever &nbsp;·&nbsp; 25,000+ LEGO® sets in the database
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/register" className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-400 text-red-800 rounded-2xl font-bold text-lg hover:from-yellow-300 hover:to-amber-300 transition-all shadow-xl hover:-translate-y-1 duration-300">
                            <FontAwesomeIcon icon={faUserCircle} className="mr-2" />
                            Create Free Account
                        </Link>
                        <Link to="/login" className="px-8 py-4 bg-white bg-opacity-20 text-white hover:text-white rounded-2xl font-bold text-lg border-2 border-white border-opacity-30 hover:bg-opacity-30 transition-all backdrop-blur-sm hover:-translate-y-1 duration-300">
                            Sign In
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Mock Preview ─────────────────────────────────────────────── */}
            <section className="py-12 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-8">
                        <span className="inline-block px-4 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full mb-3">Example Preview</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                            This is what your {pageVerb.toLowerCase()} looks like
                        </h2>
                        <p className="text-gray-600 max-w-xl mx-auto">
                            {isWishlist
                                ? 'A clean view of every set you want, with prices and availability at a glance.'
                                : 'Every set you own, with condition tracking, aftermarket prices, and piece counts.'}
                        </p>
                    </div>

                    {/* Toolbar + compact stats (collection totals mirror UserSetsView renderStatsCards) */}
                    <div className="bg-white rounded-t-xl border border-b-0 border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={isWishlist ? faGift : faBoxOpen} className="text-red-500 text-lg" />
                                    <span className="font-bold text-gray-800">BrickMaster's {pageVerb}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => setShowPrices(p => !p)}
                                    className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm font-semibold transition-all shadow-md hover:shadow-lg border hover:scale-[1.02] ${
                                        showPrices
                                            ? 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100'
                                            : 'bg-gradient-to-r from-white to-gray-50 text-gray-700 border-gray-300 hover:from-gray-50 hover:to-gray-100'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faDollarSign} className={showPrices ? 'text-green-600' : 'text-gray-500'} />
                                    {showPrices ? 'Hide Prices' : 'Show Prices'}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setViewMode(v => (v === 'grid' ? 'list' : 'grid'))}
                                    className={`flex items-center gap-2.5 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm font-semibold transition-all shadow-md hover:shadow-lg border hover:scale-[1.02] ${
                                        viewMode === 'list'
                                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-500 hover:from-blue-600 hover:to-blue-700'
                                            : 'bg-gradient-to-r from-white to-gray-50 text-gray-700 border-gray-300 hover:from-gray-50 hover:to-gray-100'
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={viewMode === 'list' ? faGrip : faList}
                                        className={viewMode === 'list' ? 'text-blue-100' : 'text-gray-500'}
                                    />
                                    <span>{viewMode === 'list' ? 'Grid View' : 'List View'}</span>
                                </button>
                            </div>
                        </div>

                        {!isWishlist && collectionTotalsWithValue && (
                            <div className="px-4 sm:px-5 py-4 bg-gradient-to-b from-gray-50/80 to-white border-b border-gray-100">
                                <p className="text-[11px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                    Collection statistics
                                </p>
                                <div
                                    className={`grid grid-cols-2 gap-3 sm:gap-4 ${
                                        showPrices ? 'lg:grid-cols-3 xl:grid-cols-5' : 'lg:grid-cols-4'
                                    }`}
                                >
                                    {/* Total Sets — same visual language as UserSetsView, scaled down */}
                                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg sm:rounded-xl shadow border border-gray-200 p-3 sm:p-4 transition-all duration-200 hover:shadow-md">
                                        <div className="flex items-start gap-2 sm:gap-3">
                                            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white shadow-md">
                                                <FontAwesomeIcon icon={faBoxOpen} className="text-sm sm:text-base" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-gray-600 font-semibold text-[10px] sm:text-xs uppercase tracking-wide mb-0.5">
                                                    Total Sets
                                                </h3>
                                                <p className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">
                                                    {collectionTotalsWithValue.totalSetCopies.toLocaleString()}
                                                </p>
                                                <p className="text-[10px] sm:text-xs text-gray-500 font-medium truncate">
                                                    {sampleSets.length} unique models
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-br from-white to-blue-50 rounded-lg sm:rounded-xl shadow border border-blue-200 p-3 sm:p-4 transition-all duration-200 hover:shadow-md">
                                        <div className="flex items-start gap-2 sm:gap-3">
                                            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-md">
                                                <FontAwesomeIcon icon={faPuzzlePiece} className="text-sm sm:text-base" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-blue-700 font-semibold text-[10px] sm:text-xs uppercase tracking-wide mb-0.5">
                                                    Total Pieces
                                                </h3>
                                                <p className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">
                                                    {collectionTotalsWithValue.totalParts.toLocaleString()}
                                                </p>
                                                <p className="text-[10px] sm:text-xs text-blue-600 font-medium">Building bricks</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-br from-white to-purple-50 rounded-lg sm:rounded-xl shadow border border-purple-200 p-3 sm:p-4 transition-all duration-200 hover:shadow-md">
                                        <div className="flex items-start gap-2 sm:gap-3">
                                            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white shadow-md">
                                                <FontAwesomeIcon icon={faUserCircle} className="text-sm sm:text-base" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-purple-700 font-semibold text-[10px] sm:text-xs uppercase tracking-wide mb-0.5">
                                                    Minifigures
                                                </h3>
                                                <div className="flex items-baseline gap-1 sm:gap-1.5 flex-wrap">
                                                    <span className="text-lg sm:text-xl font-bold text-purple-600">
                                                        {collectionTotalsWithValue.ownedMinifigs.toLocaleString()}
                                                    </span>
                                                    <span className="text-sm text-gray-400">/</span>
                                                    <span className="text-base sm:text-lg font-semibold text-gray-600">
                                                        {collectionTotalsWithValue.expectedMinifigs.toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] sm:text-xs text-purple-600 font-medium">in your collection</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-br from-white to-emerald-50 rounded-lg sm:rounded-xl shadow border border-emerald-200 p-3 sm:p-4 transition-all duration-200 hover:shadow-md">
                                        <div className="flex items-start gap-2 sm:gap-3">
                                            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white shadow-md">
                                                <FontAwesomeIcon icon={faBox} className="text-sm sm:text-base" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-emerald-700 font-semibold text-[10px] sm:text-xs uppercase tracking-wide mb-0.5">
                                                    Sealed
                                                </h3>
                                                <p className="text-lg sm:text-xl font-bold text-emerald-600 leading-tight">
                                                    {collectionTotalsWithValue.sealedCopies.toLocaleString()}
                                                </p>
                                                <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">Sealed sets</p>
                                            </div>
                                        </div>
                                    </div>

                                    {showPrices && (
                                        <div className="bg-gradient-to-br from-white to-green-50 rounded-lg sm:rounded-xl shadow border border-green-200 p-3 sm:p-4 transition-all duration-200 hover:shadow-md">
                                            <div className="flex items-start gap-2 sm:gap-3">
                                                <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white shadow-md">
                                                    <FontAwesomeIcon icon={faPiggyBank} className="text-sm sm:text-base" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="text-green-700 font-semibold text-[10px] sm:text-xs uppercase tracking-wide mb-0.5">
                                                        Collection Value
                                                    </h3>
                                                    <p className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">
                                                        $
                                                        {collectionTotalsWithValue.totalValue.toLocaleString(undefined, {
                                                            maximumFractionDigits: 0,
                                                        })}
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-green-600 font-medium">
                                                        Market value
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Flat set display */}
                    <div className="border border-gray-200 border-b-0 shadow-sm overflow-hidden bg-white">
                        {viewMode === 'grid' ? (
                            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                                {sampleSets.map(s => (
                                    <MockSetCard key={s.set_num} set={s} isWishlist={isWishlist} showPrices={showPrices} />
                                ))}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full table-auto">
                                    <thead>
                                        <tr className="bg-gray-50 border-b-2 border-gray-200">
                                            <th className="w-14 px-2 py-3"></th>
                                            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-left whitespace-nowrap">Set #</th>
                                            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-left min-w-[140px]">Name</th>
                                            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">Year</th>
                                            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">Pieces</th>
                                            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">Figs</th>
                                            {!isWishlist && (
                                                <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                                            )}
                                            {showPrices && (
                                                <>
                                                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">Retail</th>
                                                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">Sealed $</th>
                                                    <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">Used $</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sampleSets.map((s, i) => (
                                            <MockListRow key={s.set_num} set={s} isWishlist={isWishlist} showPrices={showPrices} isFirst={i === 0} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* CTA bar */}
                    <div className="bg-white border border-t-0 border-gray-200 rounded-b-xl px-5 py-4 text-center shadow-sm">
                        <Link
                            to="/register"
                            className="inline-flex items-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white hover:text-white rounded-xl font-semibold transition-colors shadow-md"
                        >
                            <FontAwesomeIcon icon={faUserCircle} className="mr-2" />
                            Start Building Your {pageVerb}
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Features grid ─────────────────────────────────────────────── */}
            <section className="py-12 bg-gray-50 border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                            Everything your {pageVerb.toLowerCase()} needs
                        </h2>
                        <p className="text-gray-600 max-w-xl mx-auto">
                            {isWishlist
                                ? 'A powerful wishlist tool built specifically for LEGO® collectors.'
                                : 'A powerful collection manager built specifically for LEGO® collectors.'}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((f, i) => (
                            <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                <div className={`w-11 h-11 ${f.color} rounded-xl flex items-center justify-center mb-3`}>
                                    <FontAwesomeIcon icon={f.icon} />
                                </div>
                                <h3 className="font-bold text-gray-800 mb-2">{f.title}</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Who is this for ───────────────────────────────────────────── */}
            <section className="py-12 bg-white border-t border-gray-100">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Who uses MyBrickLog?</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                        <div className="p-6 rounded-xl bg-gray-50 border border-gray-100">
                            <div className="text-4xl mb-3">🧱</div>
                            <h3 className="font-bold text-gray-800 mb-2">The Casual Collector</h3>
                            <p className="text-gray-600 text-sm">You have a few dozen sets and want to track exactly what you own and stop buying duplicates at the store.</p>
                        </div>
                        <div className="p-6 rounded-xl bg-gray-50 border border-gray-100">
                            <div className="text-4xl mb-3">📦</div>
                            <h3 className="font-bold text-gray-800 mb-2">The Serious Collector</h3>
                            <p className="text-gray-600 text-sm">You have hundreds of sets, some sealed as investments, and need a real inventory system with prices, statuses, and minifigure tracking.</p>
                        </div>
                        <div className="p-6 rounded-xl bg-gray-50 border border-gray-100">
                            <div className="text-4xl mb-3">🎁</div>
                            <h3 className="font-bold text-gray-800 mb-2">Gift List Makers</h3>
                            <p className="text-gray-600 text-sm">Share your wishlist with family before holidays so they pick the exact sets you want. No more wrong themes or sets you already own.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Final CTA ─────────────────────────────────────────────────── */}
            <section className="py-12 bg-gradient-to-r from-red-600 to-red-700 text-white">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-3xl font-black mb-3">
                        Ready to track your {isWishlist ? 'wishlist' : 'collection'}?
                    </h2>
                    <p className="text-red-100 text-lg mb-8">
                        Join thousands of LEGO® collectors on MyBrickLog. Free forever.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/register" className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-400 text-red-800 rounded-2xl font-bold text-lg hover:from-yellow-300 hover:to-amber-300 transition-all shadow-xl hover:-translate-y-1 duration-300">
                            <FontAwesomeIcon icon={faUserCircle} className="mr-2" />
                            Create Free Account
                        </Link>
                        <Link to="/login" className="px-8 py-4 bg-white bg-opacity-20 text-white hover:text-white rounded-2xl font-bold text-lg border-2 border-white border-opacity-30 hover:bg-opacity-30 transition-all backdrop-blur-sm hover:-translate-y-1 duration-300">
                            Sign In
                        </Link>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default GuestLanding;
